import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Target, Eye, FileText, Download, ExternalLink, BookOpen, Users, Calendar, FileSpreadsheet, Rocket, Scale, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import * as Icons from 'lucide-react';
import { getImageUrl, getFileUrl } from '../lib/utils';

const DepartmentDetail = () => {
  const { slug } = useParams();
  const [department, setDepartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [research, setResearch] = useState([]);
  const [loadingResearch, setLoadingResearch] = useState(true);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchDepartment();
  }, [slug]);

  useEffect(() => {
    if (slug === 'research-center') {
      fetchResearch();
    }
  }, [slug]);

  const fetchDepartment = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/departments/`);
      const data = await response.json();
      const dept = data.find(d => d.slug === slug);
      setDepartment(dept);
    } catch (error) {
      console.error('Error fetching department:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchResearch = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/research/`);
      const data = await response.json();
      setResearch(data);
    } catch (error) {
      console.error('Error fetching research:', error);
    } finally {
      setLoadingResearch(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F388E]"></div>
      </div>
    );
  }

  if (!department) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#0F388E] mb-4">الإدارة أو المركز غير موجود</h2>
          <Link to="/departments">
            <Button className="bg-[#0F388E] hover:bg-[#0F388E]/90">
              عودة إلى الإدارات
              <ArrowRight className="ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = Icons[department.icon];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-36 sm:pt-40 pb-20 bg-gradient-to-br from-[#0F388E] to-[#1a4fa8]" style={{ paddingTop: '140px' }}>
        {/* Background Image or Pattern */}
        {department.background_image ? (
          <div className="absolute inset-0">
            <img 
              src={getImageUrl(department.background_image)} 
              alt={department.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#0F388E]/90 to-[#1a4fa8]/90"></div>
          </div>
        ) : (
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] "></div>
          </div>
        )}
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-white">
          <Link to="/departments" className="inline-flex items-center text-white/80 hover:text-white mb-8 transition-colors">
            <ArrowRight className="ml-2 w-5 h-5" />
            عودة إلى الإدارات والمراكز
          </Link>
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center shadow-2xl">
              {IconComponent && <IconComponent className="w-12 h-12 text-[#C9A040]" />}
            </div>
            <div>
              <span className="inline-block px-4 py-2 bg-[#C9A040] text-white text-sm rounded-full mb-3">
                {department.category}
              </span>
              <h1 className="text-5xl md:text-6xl font-bold mb-4">{department.name}</h1>
              <p className="text-xl text-white/90 leading-relaxed max-w-3xl">
                {department.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Director Section */}
          {department.director_name && (
            <Card className="mb-12 overflow-hidden border-2 border-[#C9A040]/20">
              <CardContent className="p-0">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                  {/* Director Image */}
                  <div className="lg:col-span-1 bg-gradient-to-br from-[#0F388E] to-[#1a4fa8] p-8 flex items-center justify-center">
                    {department.director_image ? (
                      <div className="relative">
                        <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-white shadow-2xl">
                          <img 
                            src={getImageUrl(department.director_image)} 
                            alt={department.director_name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="w-48 h-48 rounded-full bg-white/10 flex items-center justify-center">
                        <Users className="w-24 h-24 text-white/50" />
                      </div>
                    )}
                  </div>
                  
                  {/* Director Info */}
                  <div className="lg:col-span-2 p-8">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-3xl font-bold text-[#0F388E] mb-2">{department.director_name}</h3>
                        {department.director_title && (
                          <p className="text-lg text-[#C9A040] font-semibold mb-1">{department.director_title}</p>
                        )}
                        {department.director_degree && (
                          <p className="text-base text-gray-600">{department.director_degree}</p>
                        )}
                      </div>
                      {department.director_cv_url && (
                        <a 
                          href={getFileUrl(department.director_cv_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-[#0F388E] text-white rounded-lg hover:bg-[#0F388E]/90 transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          <span className="text-sm">السيرة الذاتية</span>
                        </a>
                      )}
                    </div>
                    
                    {department.director_message && (
                      <div className="mt-6 p-6 bg-gray-50 rounded-lg border-r-4 border-[#C9A040]">
                        <h4 className="text-lg font-bold text-[#0F388E] mb-3">كلمة المدير</h4>
                        <p className="text-gray-700 leading-relaxed">{department.director_message}</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* About Section (if exists) */}
          {department.about && (
            <Card className="mb-12">
              <CardContent className="p-8">
                <h3 className="text-3xl font-bold text-[#0F388E] mb-6">نبذة عن {department.name}</h3>
                <p className="text-gray-700 leading-relaxed text-lg">{department.about}</p>
              </CardContent>
            </Card>
          )}

          {/* Vision & Mission */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <Card className="border-t-4 border-t-[#0F388E] hover:shadow-xl transition-all">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-[#0F388E] rounded-xl flex items-center justify-center mb-6">
                  <Eye className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#0F388E] mb-4">الرؤية</h3>
                <p className="text-gray-700 leading-relaxed">{department.vision}</p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-t-[#C9A040] hover:shadow-xl transition-all">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-[#C9A040] rounded-xl flex items-center justify-center mb-6">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#0F388E] mb-4">الرسالة</h3>
                <p className="text-gray-700 leading-relaxed">{department.mission}</p>
              </CardContent>
            </Card>
          </div>

          {/* Objectives */}
          <Card className="mb-12">
            <CardContent className="p-8">
              <h3 className="text-3xl font-bold text-[#0F388E] mb-6">الأهداف</h3>
              <p className="text-gray-700 leading-relaxed text-lg">{department.objectives}</p>
            </CardContent>
          </Card>

          {/* Responsibilities */}
          <Card className="mb-12">
            <CardContent className="p-8">
              <h3 className="text-3xl font-bold text-[#0F388E] mb-6">المهام والمسؤوليات</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {department.responsibilities.map((responsibility, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-white transition-colors">
                    <CheckCircle2 className="w-6 h-6 text-[#C9A040] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{responsibility}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* نماذج واستمارات Section */}
          {department.forms && department.forms.length > 0 && (
            <Card className="mb-12 border-t-4 border-t-[#0F388E]">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#0F388E] to-[#1a4fa8] rounded-xl flex items-center justify-center">
                    <FileSpreadsheet className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-[#0F388E]">نماذج واستمارات</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {department.forms.map((form, index) => (
                    <a
                      key={index}
                      href={getFileUrl(form.file_url || form.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl p-6 hover:border-[#C9A040] hover:shadow-xl transition-all duration-300"
                    >
                      <div className="flex flex-col items-center text-center">
                        <div className="w-16 h-16 bg-gradient-to-br from-[#0F388E] to-[#1a4fa8] rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                          <FileText className="w-8 h-8 text-white" />
                        </div>
                        <h4 className="font-bold text-gray-900 mb-1 group-hover:text-[#0F388E] transition-colors">
                          {form.title_ar || form.title}
                        </h4>
                        {(form.title_en || form.title) && (
                          <p className="text-xs text-gray-500 mb-3">{form.title_en || form.title}</p>
                        )}
                        <div className="flex items-center gap-2 text-[#C9A040] font-semibold text-sm">
                          <Download className="w-4 h-4" />
                          <span>تحميل PDF</span>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* برامج ومبادرات Section */}
          {department.programs && department.programs.length > 0 && (
            <Card className="mb-12 border-t-4 border-t-[#C9A040]">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#C9A040] to-[#b8903a] rounded-xl flex items-center justify-center">
                    <Rocket className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-[#0F388E]">برامج ومبادرات</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {department.programs.map((program, index) => (
                    <a
                      key={index}
                      href={program.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-between p-6 bg-gradient-to-r from-white to-gray-50 border-2 border-gray-200 rounded-xl hover:border-[#C9A040] hover:shadow-xl transition-all duration-300"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className="w-14 h-14 bg-gradient-to-br from-[#C9A040] to-[#b8903a] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <Rocket className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 mb-1 group-hover:text-[#0F388E] transition-colors">
                            {program.title_ar || program.title}
                          </h4>
                          {(program.title_en || program.title) && (
                            <p className="text-sm text-gray-500">{program.title_en || program.title}</p>
                          )}
                        </div>
                      </div>
                      <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-[#C9A040] transition-colors" />
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* لوائح وأدلة Section */}
          {department.regulations && department.regulations.length > 0 && (
            <Card className="mb-12 border-t-4 border-t-[#0F388E]">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#0F388E] to-[#1a4fa8] rounded-xl flex items-center justify-center">
                    <Scale className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-[#0F388E]">لوائح وأدلة</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {department.regulations.map((regulation, index) => (
                    <a
                      key={index}
                      href={getFileUrl(regulation.file_url || regulation.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative bg-gradient-to-br from-white to-blue-50 border-2 border-gray-200 rounded-xl p-6 hover:border-[#0F388E] hover:shadow-xl transition-all duration-300"
                    >
                      <div className="flex flex-col items-center text-center">
                        <div className="w-16 h-16 bg-gradient-to-br from-[#0F388E] to-[#1a4fa8] rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                          <Scale className="w-8 h-8 text-white" />
                        </div>
                        <h4 className="font-bold text-gray-900 mb-1 group-hover:text-[#0F388E] transition-colors">
                          {regulation.title_ar || regulation.title}
                        </h4>
                        {(regulation.title_en || regulation.title) && (
                          <p className="text-xs text-gray-500 mb-3">{regulation.title_en || regulation.title}</p>
                        )}
                        <div className="flex items-center gap-2 text-[#0F388E] font-semibold text-sm">
                          <Download className="w-4 h-4" />
                          <span>تحميل PDF</span>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* التقارير السنوية Section */}
          {department.annual_reports && department.annual_reports.length > 0 && (
            <Card className="mb-12 border-t-4 border-t-[#C9A040]">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#C9A040] to-[#b8903a] rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-[#0F388E]">التقارير السنوية</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {department.annual_reports.map((report, index) => (
                    <a
                      key={index}
                      href={getFileUrl(report.file_url || report.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative bg-gradient-to-br from-white to-yellow-50 border-2 border-gray-200 rounded-xl p-6 hover:border-[#C9A040] hover:shadow-xl transition-all duration-300"
                    >
                      <div className="flex flex-col items-center text-center">
                        {report.year && (
                          <div className="absolute top-3 right-3 bg-[#C9A040] text-white text-xs font-bold px-3 py-1 rounded-full">
                            {report.year}
                          </div>
                        )}
                        <div className="w-16 h-16 bg-gradient-to-br from-[#C9A040] to-[#b8903a] rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                          <BarChart3 className="w-8 h-8 text-white" />
                        </div>
                        <h4 className="font-bold text-gray-900 mb-1 group-hover:text-[#C9A040] transition-colors">
                          {report.title_ar || report.title}
                        </h4>
                        {(report.title_en || report.title) && (
                          <p className="text-xs text-gray-500 mb-3">{report.title_en || report.title}</p>
                        )}
                        <div className="flex items-center gap-2 text-[#C9A040] font-semibold text-sm">
                          <Download className="w-4 h-4" />
                          <span>تحميل</span>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Published Research Section (for research-center only) */}
          {slug === 'research-center' && (
            <Card className="border-2 border-blue-100 shadow-md">
              <CardContent className="p-8">
                {/* Header with Call to Action to Dedicated Page */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 pb-6 border-b border-gray-150">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl bg-[#0F388E]/10 flex items-center justify-center text-[#0F388E]">
                        <BookOpen className="w-5 h-5" />
                      </div>
                      <h3 className="text-2xl sm:text-3xl font-bold text-[#0F388E]">الأبحاث العلمية المنشورة</h3>
                    </div>
                    <p className="text-sm text-gray-500">
                      أبحاث ودراسات محكمة منشورة لأعضاء هيئة التدريس والباحثين في جامعة ابن النفيس
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {research.length > 0 && (
                      <span className="text-xs font-bold text-[#0F388E] bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-full whitespace-nowrap">
                        {research.length} بحثاً منشوراً
                      </span>
                    )}
                    <Link to="/research">
                      <Button className="bg-gradient-to-r from-[#0F388E] to-[#1c53b8] hover:from-[#123e9c] hover:to-[#2360cd] text-white font-semibold text-sm shadow-md hover:shadow-lg transition-all flex items-center gap-2">
                        <span>عرض جميع الأبحاث في صفحة خاصة</span>
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
                
                {loadingResearch ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F388E]"></div>
                  </div>
                ) : research.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">لا توجد أبحاث منشورة حالياً</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                      {research.slice(0, 6).map((item, idx) => (
                        <Card key={item.id} className="border border-gray-200 hover:border-[#C9A040] hover:shadow-xl transition-all flex flex-col justify-between group">
                          <CardContent className="p-6">
                            <div className="flex items-start gap-4 mb-4">
                              <div className="w-12 h-12 bg-gradient-to-br from-[#0F388E] to-[#1a4fa8] rounded-xl flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">
                                #{idx + 1}
                              </div>
                              <div className="flex-1">
                                <h4 className="text-base font-bold text-gray-900 leading-snug mb-1 group-hover:text-[#0F388E] transition-colors">
                                  {item.title_ar}
                                </h4>
                                {item.journal_name && (
                                  <span className="inline-block text-xs font-semibold text-[#C9A040] bg-amber-50 border border-amber-200/60 px-2 py-0.5 rounded-md mt-1">
                                    {item.journal_name}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="space-y-2 mb-4 text-xs text-gray-600">
                              {Array.isArray(item.authors) && item.authors.length > 0 && (
                                <div className="flex items-start gap-2">
                                  <Users className="w-3.5 h-3.5 text-[#C9A040] mt-0.5 flex-shrink-0" />
                                  <span className="leading-relaxed">{item.authors.join('، ')}</span>
                                </div>
                              )}

                              {item.publication_date && (
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-3.5 h-3.5 text-[#C9A040] flex-shrink-0" />
                                  <span>تاريخ النشر: {item.publication_date}</span>
                                </div>
                              )}
                            </div>

                            {item.abstract_ar && (
                              <p className="text-xs text-gray-500 leading-relaxed mb-4 line-clamp-3">
                                {item.abstract_ar}
                              </p>
                            )}

                            <div className="flex items-center gap-2 pt-3 border-t border-gray-100 mt-auto flex-wrap">
                              {item.pdf_url && (
                                <a
                                  href={item.pdf_url.startsWith('http') ? item.pdf_url : `${BACKEND_URL}${item.pdf_url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0F388E] text-white text-xs font-semibold rounded-lg hover:bg-[#0F388E]/90 transition-colors"
                                >
                                  <Download className="w-3.5 h-3.5" />
                                  <span>تحميل PDF</span>
                                </a>
                              )}
                              {item.research_url && (
                                <a
                                  href={item.research_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                  <ExternalLink className="w-3.5 h-3.5 text-[#C9A040]" />
                                  <span>رابط البحث</span>
                                </a>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {/* Bottom Link to Full Page */}
                    <div className="text-center pt-4">
                      <Link to="/research">
                        <Button variant="outline" className="border-2 border-[#0F388E] text-[#0F388E] hover:bg-[#0F388E] hover:text-white font-bold px-8 py-3 rounded-xl transition-all">
                          عرض جميع الأبحاث المنشورة ({research.length} بحثاً) مع إمكانية البحث والتصفية
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </section>
    </div>
  );
};

export default DepartmentDetail;